/* eslint-disable camelcase */
import Decimal from 'decimal.js'
import {
  applyDiscount,
  duplicateTime,
  getPercetageApplicated,
  truncateNumber,
} from '../../../util/utils'

const discountValue = (defaulValue, finalValue) => {
  return defaulValue - finalValue
}

// Converte valores vindos dos inputs (string) em número seguro.
// Estados intermediários do NumberFormat como "-" ou "" viram NaN e, ao
// chegarem no Decimal, quebram o componente — aqui normalizamos para 0.
const toNumber = value => {
  const parsed = parseFloat(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export const updateDiscountOrQuantity = (item, general_discount) => {
  const { default_value, quantity, discount, execution_time, attendanceType } =
    item

  if (attendanceType?.id == 2) {
    item.final_time = duplicateTime([execution_time], quantity)
  }

  const safeDefault = toNumber(default_value)
  const safeQuantity = toNumber(quantity)

  item.final_value = safeQuantity * safeDefault

  if (general_discount) return

  let discountNumber = toNumber(discount)

  // Desconto não pode ser negativo (acréscimo). Limita ao mínimo de 0%.
  if (discountNumber < 0) {
    item.discount = ''
    discountNumber = 0
  }

  if (!discountNumber) {
    return (item.discount_value = '')
  }

  const defaulValue = safeDefault * safeQuantity
  const finalValueDiscount = applyDiscount(defaulValue, discountNumber)

  const finalValue = new Decimal(finalValueDiscount).toFixed(2)
  item.discount_value = discountValue(defaulValue, finalValue)
  return (item.final_value = finalValue)
}

export const updateMeasures = item => {
  const { base, altura, type, diametro } = item.options

  let quantity = 0

  if (type == 1) {
    quantity = Number(base || 1) * Number(altura || 1)
  } else {
    quantity = Math.round(((Math.PI * diametro ** 2) / 4) * 100) / 100
  }

  return (item.quantity = parseFloat(quantity).toFixed(2))
}

/**
 * Identificador único de um item de serviço na tela (key do React, DnD e
 * remoção). Não dá para usar só o `id`: em item novo o `id` é o id do
 * service_type, então dois itens do mesmo serviço colidiriam.
 *
 * - Sem `dateTime` -> item já persistido, o `id` é o id da linha no banco.
 * - Com `dateTime` -> item novo (adicionado no select ou vindo de cópia de OS),
 *   onde o `id` é o do service_type. Confirmamos isso por `serviceType.id`
 *   quando a relação existe (cópia de OS); no item vindo do select não há
 *   `serviceType`, e aí o próprio `dateTime` já basta.
 */
export const getServiceItemKey = item => {
  const { id, dateTime, serviceType } = item || {}

  if (!dateTime) return String(id)

  if (!serviceType || String(serviceType.id) === String(id))
    return String(dateTime)

  return String(id)
}

/**
 * @author Heitor Santos 03/08/2026
 * Regra de negócio: item em status final NÃO pode ir para retrabalho — nesse
 * caso o correto é abrir uma nova OS. Este array é o único ponto de mudança caso
 * os ids de status de item mudem no banco.
 * 110 -> Entregue | 120 -> Concluído | 130 -> Cancelado
 */
export const REWORK_BLOCKED_STATUS_IDS = [110, 120, 130]

export const REWORK_BLOCKED_MESSAGE =
  'Item entregue, concluído ou cancelado não pode ser marcado como retrabalho. Gere uma nova OS.'

export const isItemFinalized = item =>
  REWORK_BLOCKED_STATUS_IDS.includes(
    Number(item?.status_id ?? item?.status?.id)
  )

/**
 * Só bloqueia a marcação. Desmarcar segue liberado, senão item em status final
 * que já está em retrabalho (dado legado) ficaria travado com valor zerado.
 */
export const isReworkBlocked = item => !item?.is_rework && isItemFinalized(item)

export const updateRework = item => {
  const { is_rework, quantity, serviceType, base_price } = item

  if (!serviceType && base_price) {
    item.default_value = is_rework ? 0 : Number(base_price)
    item.final_value = is_rework ? 0 : Number(base_price) * quantity

    return item
  }

  item.default_value = is_rework ? 0 : Number(serviceType?.default_value)
  item.final_value = is_rework
    ? 0
    : Number(serviceType?.default_value) * quantity

  return item
}

export const updateFinalValue = item => {
  const { quantity, default_value, final_value } = item

  const defaultFinalValue = (toNumber(quantity) || 1) * toNumber(default_value)
  let finalValueNumber = toNumber(final_value)

  // O Total não pode exceder o valor calculado do item (sem desconto), pois
  // isso geraria um "desconto" negativo. Limita ao teto → desconto mínimo 0%.
  if (finalValueNumber > defaultFinalValue) {
    finalValueNumber = defaultFinalValue
    item.final_value = new Decimal(defaultFinalValue).toFixed(2)
  }

  const discount = getPercetageApplicated(
    defaultFinalValue || 0,
    finalValueNumber || 0
  ).toFixed(2)

  const discount_value = new Decimal(defaultFinalValue || 0)
    .minus(new Decimal(finalValueNumber || 0))
    .toFixed(2)

  item.discount = discount
  item.discount_value = discount_value

  return item
}

export const applicateMultipleDiscount = (
  services,
  finalValueDiscount,
  noDiscountValue,
  discount
) => {
  const lastPosition = services?.length - 1

  let amount = new Decimal(0)

  services?.map((item, index) => {
    const { default_value, quantity } = item

    const defaultValue = quantity * default_value

    if (index == lastPosition) {
      const finalValue = new Decimal(finalValueDiscount)
        .minus(amount.toNumber())
        .toFixed(2)

      const discountApplicated = getPercetageApplicated(
        defaultValue,
        finalValue
      )

      item.discount = new Decimal(discountApplicated || 0).eq(0)
        ? ''
        : discountApplicated.toFixed(2)
      item.final_value = finalValue

      return (item.discount_value = discountValue(defaultValue, finalValue))
    }

    const finalValue = new Decimal(defaultValue)
      .times(finalValueDiscount)
      .dividedBy(noDiscountValue)
      .toFixed(2)

    item.final_value = finalValue
    item.discount = discount
    item.discount_value = discountValue(defaultValue, finalValue)

    amount = amount.add(finalValue)
  })

  return services
}
